(() => {
  const STORAGE_KEY = 'recently_viewed_products_v1';
  const MAX_ITEMS = 20;

  function readStorage() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch (e) {
      return [];
    }
  }

  function writeStorage(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      /* noop */
    }
  }

  function recordProduct(product) {
    if (!product || !product.handle) return;
    const items = readStorage();
    const existingIndex = items.findIndex((p) => p.handle === product.handle);
    if (existingIndex !== -1) return; // do nothing if already present

    // push to front (most recent first)
    const next = [product, ...items];
    if (next.length > MAX_ITEMS) next.length = MAX_ITEMS; // trim oldest
    writeStorage(next);
  }

  // Expose a global helper for PDP hook
  window.RecentlyViewed = {
    recordProduct,
    read: readStorage
  };

  class RecentlyViewedElement extends HTMLElement {
    constructor() {
      super();
      this.grid = null;
      this.count = 4;
      this.currentHandle = null;
      this._initialized = false;
    }

    connectedCallback() {
      this.count = parseInt(this.dataset.count || '4', 10);
      this.layout = this.dataset.layout || 'grid';
      this.columns = parseInt(this.dataset.columns || '4', 10);
      // Get current product handle from body or meta tag if available
      const currentHandleEl = document.querySelector('[data-product-handle]');
      this.currentHandle = currentHandleEl ? currentHandleEl.getAttribute('data-product-handle') : null;

      // Wait until children are parsed so [data-grid] exists
      const tryInit = () => {
        if (this._initialized) return;
        this.grid = this.querySelector('[data-grid]');
        if (this.grid) {
          this._initialized = true;
          this.render();
          return;
        }
        // If still not found, observe for child additions briefly
        const mo = new MutationObserver(() => {
          this.grid = this.querySelector('[data-grid]');
          if (this.grid) {
            mo.disconnect();
            this._initialized = true;
            this.render();
          }
        });
        mo.observe(this, { childList: true, subtree: true });
        // Safety: also try on next frame
        requestAnimationFrame(() => {
          if (!this._initialized) {
            this.grid = this.querySelector('[data-grid]');
            if (this.grid) {
              mo.disconnect();
              this._initialized = true;
              this.render();
            }
          }
        });
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', tryInit, { once: true });
      } else {
        tryInit();
      }
    }

    render() {
      if (!this.grid) return;
      const showVendor = this.dataset.showVendor === 'true';
      const showStock = this.dataset.showStock === 'true';
      const showRating = this.dataset.showRating === 'true';

      const items = readStorage()
        .filter((p) => (this.currentHandle ? p.handle !== this.currentHandle : true))
        .slice(0, MAX_ITEMS);

      // Build list
      this.grid.innerHTML = '';
      if (items.length === 0) {
        this.closest('.block-section')?.classList.add('is-empty');
        return;
      }

      // Don't overwrite existing classes - the HTML already has the correct layout classes
      // including 'grid-carousel' for slider layout. Just ensure we don't lose them.
      if (!this.grid.classList.contains('grid')) {
        this.grid.classList.add('grid');
      }
      
      // Ensure proper column classes are set (in case they're missing)
      if (this.columns >= 5) {
        this.grid.classList.add('grid-3-col-tablet', 'grid-5-col-desktop', 'grid-compact');
      } else if (this.columns === 4) {
        this.grid.classList.add('grid-3-col-tablet', 'grid-4-col-desktop');
      } else {
        this.grid.classList.add('grid-3-col-tablet', 'grid-3-col-desktop');
      }
      
      // Ensure grid-carousel class exists for slider (should already be there from Liquid)
      if (this.layout === 'slider' && !this.grid.classList.contains('grid-carousel')) {
        this.grid.classList.add('grid-carousel');
      }

      const itemTag = this.layout === 'slider' ? 'div' : 'li';
      items.forEach((p) => {
        const el = document.createElement(itemTag);
        el.className = 'grid-item';
        el.innerHTML = this.cardTemplate(p, { showVendor, showStock, showRating });
        this.grid.appendChild(el);
      });
      // For slider layout, carousel-slider will automatically initialize when content is added
      // No additional action needed
    }

    cardTemplate(product, opts) {
      // Match the exact structure of card-product snippet
      const image = product.featured_image || null;
      const priceHtml = product.price_html || '';
      
      return `
        <product-card class="card card-product${!image ? ' card-no-media' : ''} color-background-1">
          <figure class="card-media">
            <a href="${product.url}" class="js-product-link">
              ${image ? `
                <div class="aspect-ratio" style="--ratio-percent: 100%;">
                  <img class="card-media-image" 
                       src="${image}" 
                       loading="lazy" 
                       alt="${this.escape(product.title)}"
                       sizes="(min-width: 1200px) 420px, (min-width: 750px) calc((100vw - 4rem - 3rem) / 3), calc(100vw - 3rem - 1rem)">
                </div>
              ` : ''}
            </a>
          </figure>
          <div class="card-info">
            <h3 class="card-heading">
              <a class="js-product-link" href="${product.url}">${this.escape(product.title)}</a>
            </h3>
            ${opts.showVendor && product.vendor ? `
              <div class="card-eyebrow">${this.escape(product.vendor)}</div>
            ` : ''}
          </div>
          <div class="card-footer">
            <div class="card-footer-intro${opts.showRating ? ' product-rating-hide-empty' : ''}">
              ${opts.showStock ? `
                <div class="badge-stock badge-stock-in">
                  <span class="badge-stock-dot"></span> In Stock
                </div>
              ` : ''}
              ${opts.showRating ? `
                <div class="rating" role="img" aria-label="Rating">
                  <!-- Rating stars would go here -->
                </div>
              ` : ''}
            </div>
            ${priceHtml ? `<div class="price">${priceHtml}</div>` : ''}
          </div>
        </product-card>
      `;
    }

    escape(s) {
      return String(s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

  }

  if (!customElements.get('recently-viewed')) {
    customElements.define('recently-viewed', RecentlyViewedElement);
  }
})();


